import { Typography } from "@mui/material";
import {
	CoreBackButton,
	CoreButton,
	CorePaper,
	CoreRadioCard,
	CoreRadioCardSelectIcon,
	CoreRequiredIcon,
	CoreTextField,
} from "../../core";
import i18nPackage from "../../i18n/i18n";
import styles from "./SelectAccommodationPage.module.css";
import { EditQuizExpandButton } from "../components/EditQuizButton";
import { forwardRef, useEffect, useId, useMemo, useState } from "react";
import ProfileList, { ProfileListProps } from "../profile/ProfileList";
import { ConditionalWrapper } from "../../wrapper";
import { useMobileLayout, useScrollIntoViewWithCondition } from "../../hooks";
import { StepTitleCard } from "../edit-quiz-steps/CommonStepComponents";

type ProctorButtonProps = {
	checked: boolean;
	onClick: () => void;
	title: string;
	description: string;
};

function ProctorButton({ checked, description, onClick, title }: ProctorButtonProps) {
	return (
		<CoreRadioCard
			onClick={onClick}
			selected={checked}
			slotProps={{
				button: { className: styles.proctorButton },
				container: { className: styles.proctorButtonContainer },
			}}
		>
			<div className={styles.proctorInfoCol}>
				<Typography className={styles.title}>{title}</Typography>
				<Typography className={styles.description}>{description}</Typography>
			</div>
			<CoreRadioCardSelectIcon selected={checked} />
		</CoreRadioCard>
	);
}

type ProfileSectionProps = {
	isMobileLayout: boolean;
} & Pick<
	ProfileListProps,
	| "profiles"
	| "selectedId"
	| "onProfileSelected"
	| "optionRelatedInstituteSettings"
	| "verityInstituteSettings"
	| "enforcedProfilesOnly"
	| "proctoringData"
	| "canDeleteProfile"
	| "onDelete"
	| "lms"
>;

function ProfileSection({ isMobileLayout, ...props }: ProfileSectionProps) {
	const [isViewingProfile, setIsViewingProfile] = useState(false);
	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<CorePaper component="section" className={styles.profileSection}>
					{children}
				</CorePaper>
			)}
			whenFalse={(children) => <SectionWrapper>{children}</SectionWrapper>}
		>
			<SectionTitle>
				{i18nPackage.t("editQuizPage.accommodationModal.addProfileHeader")}
				<span style={{ marginLeft: "0.3125rem" }}>
					<CoreRequiredIcon />
				</span>
			</SectionTitle>
			{!isViewingProfile && (
				<SectionDescription>{i18nPackage.t("editQuizPage.accommodationModal.addProfileLabel")}</SectionDescription>
			)}
			<ProfileList
				{...props}
				showCustomButton={false}
				onViewProfile={(profile) => setIsViewingProfile(Boolean(profile))}
			/>
		</ConditionalWrapper>
	);
}

function SectionDescription({ children, id }: { children: React.ReactNode; id?: string }) {
	return (
		<Typography className={styles.sectionDescription} id={id}>
			{children}
		</Typography>
	);
}

function SectionTitle({ children }: { children: React.ReactNode }) {
	return (
		<Typography variant="h2" className={styles.sectionHeader}>
			{children}
		</Typography>
	);
}

type SectionWrapperProps = {
	children: React.ReactNode;
	className?: string;
};

const SectionWrapper = forwardRef<HTMLDivElement, SectionWrapperProps>(function SectionWrapper(
	{ children, className },
	ref,
) {
	return (
		<div ref={ref} className={`${styles.sectionWrapper} ${className || ""}`}>
			{children}
		</div>
	);
});

function AccommodationHeader({ isMobileLayout }: { isMobileLayout: boolean }) {
	const title = i18nPackage.t("editQuizPage.accommodationModal.title");
	const description = i18nPackage.t("editQuizPage.accommodationModal.description");

	if (isMobileLayout) {
		return <StepTitleCard title={title} description={description} />;
	}

	return (
		<div className={styles.headerWrapper}>
			<Typography variant="h2" className={styles.title}>
				{title}
			</Typography>
			<SectionDescription>{description}</SectionDescription>
		</div>
	);
}

type ProctorButtonType = "doNotProctor" | "modifyProctor";

export type AccommodationChangePayload = {
	proctorType: ProctorButtonType;
	selectedProfileId: number | undefined;
	selectedNotes: string | undefined;
};

export type SelectAccommodationPageProps = {
	selectedProfileId: number | undefined;
	studentAccommodationNotes: string | undefined;
	onChange: (payload: AccommodationChangePayload) => void;
	onBack: () => void;
	onSave?: () => void;
	onInvalidDraft?: (invalid: boolean) => void;
} & Pick<
	ProfileSectionProps,
	| "enforcedProfilesOnly"
	| "optionRelatedInstituteSettings"
	| "verityInstituteSettings"
	| "proctoringData"
	| "canDeleteProfile"
	| "onDelete"
	| "profiles"
	| "lms"
>;

export default function SelectAccommodationPage({
	selectedProfileId,
	studentAccommodationNotes,
	onChange,
	onBack,
	onSave,
	onInvalidDraft,
	...props
}: SelectAccommodationPageProps) {
	const [proctorType, setProctorType] = useState<ProctorButtonType>(
		selectedProfileId ? "modifyProctor" : "doNotProctor",
	);
	const [isSaving, setIsSaving] = useState(false);
	const isMobileLayout = useMobileLayout();

	const shouldScroll = useMemo(() => proctorType === "modifyProctor", [proctorType]);
	const { ref } = useScrollIntoViewWithCondition<HTMLDivElement>(shouldScroll);

	const handleProctorTypeToggle = (val: ProctorButtonType) => {
		if (val !== proctorType) {
			onChange({
				proctorType: val,
				selectedProfileId: val === "modifyProctor" ? selectedProfileId : undefined,
				selectedNotes: studentAccommodationNotes,
			});
			setProctorType(val);
		}
	};

	const handleProfileSelect = (profileId: number | undefined) => {
		onChange({
			proctorType,
			selectedProfileId: profileId,
			selectedNotes: studentAccommodationNotes,
		});
	};

	const handleNotesChange = (text: string) => {
		onChange({
			proctorType,
			selectedProfileId,
			selectedNotes: text,
		});
	};

	const handleSave = () => {
		try {
			setIsSaving(true);
			onSave?.();
		} finally {
			setIsSaving(false);
		}
	};

	const profileSectionProps = {
		...props,
		isMobileLayout,
		onProfileSelected: handleProfileSelect,
		selectedId: selectedProfileId,
	};

	const accommodationRadioAriaId = useId();

	const isSaveDisabled = isSaving || (proctorType === "modifyProctor" && !selectedProfileId);

	useEffect(() => {
		if (onInvalidDraft) {
			onInvalidDraft(isSaveDisabled);
		}
		return () => {
			onInvalidDraft?.(false);
		};
	}, [isSaveDisabled, onInvalidDraft]);

	return (
		<div className={styles.page}>
			<div className={styles.topRow}>
				<CoreBackButton onBack={onBack} text={i18nPackage.t("editQuizPage.accommodationModal.backButton")} />
				{onSave && (
					<CoreButton onClick={handleSave} className={styles.saveButton} disabled={isSaveDisabled} loading={isSaving}>
						{i18nPackage.t("editQuizPage.accommodationModal.saveAccommodations")}
					</CoreButton>
				)}
			</div>
			<AccommodationHeader isMobileLayout={isMobileLayout} />
			<SectionWrapper>
				<SectionTitle>{i18nPackage.t("editQuizPage.accommodationModal.setAccommodation")}</SectionTitle>
				<SectionDescription id={accommodationRadioAriaId}>
					{i18nPackage.t("editQuizPage.accommodationModal.section1Header")}
				</SectionDescription>
				<div className={styles.radioCardWrapper}>
					<ProctorButton
						title={i18nPackage.t("editQuizPage.accommodationModal.doNotProctorTitle")}
						description={i18nPackage.t("editQuizPage.accommodationModal.doNotProctorLabel")}
						onClick={() => handleProctorTypeToggle("doNotProctor")}
						checked={proctorType === "doNotProctor"}
					/>
					<ProctorButton
						title={i18nPackage.t("editQuizPage.accommodationModal.modifyTitle")}
						description={i18nPackage.t("editQuizPage.accommodationModal.modifyLabel")}
						onClick={() => handleProctorTypeToggle("modifyProctor")}
						checked={proctorType === "modifyProctor"}
					/>
				</div>
			</SectionWrapper>
			{(proctorType === "doNotProctor" || proctorType === "modifyProctor") && (
				<SectionWrapper className={styles.notesSectionWrapper} ref={ref}>
					{proctorType === "modifyProctor" && <ProfileSection {...profileSectionProps} />}
					<EditQuizExpandButton
						title={i18nPackage.t("editQuizPage.accommodationModal.notesHeader")}
						description={i18nPackage.t("editQuizPage.accommodationModal.notesLabel")}
						defaultOpen={true}
						slotProps={{
							title: {
								className: styles.sectionHeader,
								style: {
									borderBottom: "0",
								},
							},
							description: {
								className: styles.sectionDescription,
							},
						}}
					>
						<CoreTextField
							value={studentAccommodationNotes}
							label={i18nPackage.t("editQuizPage.accommodationModal.textFieldLabel")}
							onChange={(e) => handleNotesChange(e.target.value)}
							className={styles.notesSectionContentTextBox}
							multiline
							InputProps={{
								inputComponent: "textarea",
							}}
						/>
					</EditQuizExpandButton>
				</SectionWrapper>
			)}
		</div>
	);
}
